import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Records how a candidate behaves while a test is open: leaving the tab or
 * the browser window, time spent away, the pointer leaving the page and
 * copy/paste attempts.
 *
 * A browser cannot lock the screen or see a second device (a phone), so this
 * is evidence for HR, not a lock. In strict mode copy/paste is blocked and the
 * test is ended once the leave or away-time limit is exceeded.
 */
export const DEFAULT_INTEGRITY_POLICY = {
    strict: false,
    max_away_seconds: 300,
    max_tab_switches: 10,
};

const MAX_EVENTS = 200;

const emptyStats = () => ({
    tab_switches: 0,
    window_blurs: 0,
    away_seconds: 0,
    longest_away_seconds: 0,
    mouse_leaves: 0,
    copy_attempts: 0,
    paste_attempts: 0,
    context_menu_attempts: 0,
    fullscreen_exits: 0,
    terminated: false,
    terminated_reason: null,
    events: [],
});

export function normalizePolicy(raw) {
    const source = raw && typeof raw === "object" ? raw : {};
    const away = Number(source.max_away_seconds);
    const switches = Number(source.max_tab_switches);
    return {
        strict: source.strict === true,
        max_away_seconds: Number.isFinite(away) && away > 0 ? away : DEFAULT_INTEGRITY_POLICY.max_away_seconds,
        max_tab_switches: Number.isFinite(switches) && switches > 0 ? switches : DEFAULT_INTEGRITY_POLICY.max_tab_switches,
    };
}

export function leaveCount(stats) {
    return Number(stats?.tab_switches || 0) + Number(stats?.window_blurs || 0) + Number(stats?.fullscreen_exits || 0);
}

export function limitReason(stats, policy) {
    if (!policy?.strict) return null;
    if (leaveCount(stats) > policy.max_tab_switches) return "tab_switches";
    if (Number(stats?.away_seconds || 0) >= policy.max_away_seconds) return "away_time";
    return null;
}

function isTouchDevice() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    try {
        return window.matchMedia("(pointer: coarse)").matches;
    } catch (_) {
        return false;
    }
}

export function useTestIntegrity({ active, policy: rawPolicy, onLimitExceeded }) {
    const policy = normalizePolicy(rawPolicy);
    const statsRef = useRef(emptyStats());
    const awaySinceRef = useRef(null);
    const startedAtRef = useRef(null);
    const limitHandledRef = useRef(false);
    const onLimitRef = useRef(onLimitExceeded);
    const [warning, setWarning] = useState(null);

    useEffect(() => {
        onLimitRef.current = onLimitExceeded;
    }, [onLimitExceeded]);

    const log = (type, extra = {}) => {
        const events = statsRef.current.events;
        if (events.length >= MAX_EVENTS) return;
        events.push({
            type,
            at_seconds: startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0,
            ...extra,
        });
    };

    const checkLimit = useCallback(() => {
        const reason = limitReason(statsRef.current, policy);
        if (!reason || limitHandledRef.current) return;
        limitHandledRef.current = true;
        statsRef.current.terminated = true;
        statsRef.current.terminated_reason = reason;
        log("terminated", { reason });
        if (typeof onLimitRef.current === "function") onLimitRef.current(reason);
    }, [policy.strict, policy.max_away_seconds, policy.max_tab_switches]); // eslint-disable-line react-hooks/exhaustive-deps

    const startAway = (type) => {
        if (awaySinceRef.current != null) return;
        awaySinceRef.current = Date.now();
        log(type);
    };

    const endAway = () => {
        if (awaySinceRef.current == null) return;
        const seconds = Math.max(0, Math.round((Date.now() - awaySinceRef.current) / 1000));
        awaySinceRef.current = null;
        const stats = statsRef.current;
        stats.away_seconds += seconds;
        stats.longest_away_seconds = Math.max(stats.longest_away_seconds, seconds);
        log("returned", { away_seconds: seconds });
        if (policy.strict) {
            setWarning({
                leaves: leaveCount(stats),
                maxLeaves: policy.max_tab_switches,
                awaySeconds: stats.away_seconds,
                maxAwaySeconds: policy.max_away_seconds,
            });
        }
        checkLimit();
    };

    useEffect(() => {
        if (!active) return undefined;
        if (startedAtRef.current == null) startedAtRef.current = Date.now();
        const stats = statsRef.current;

        const onVisibility = () => {
            if (document.hidden) {
                stats.tab_switches += 1;
                startAway("tab_hidden");
            } else {
                endAway();
            }
        };
        const onBlur = () => {
            if (document.hidden || awaySinceRef.current != null) return;
            stats.window_blurs += 1;
            startAway("window_blur");
        };
        const onFocus = () => {
            if (!document.hidden) endAway();
        };
        const onMouseLeave = () => {
            stats.mouse_leaves += 1;
            log("mouse_leave");
        };
        const onCopy = (event) => {
            stats.copy_attempts += 1;
            log(event.type);
            if (policy.strict) event.preventDefault();
        };
        const onPaste = (event) => {
            stats.paste_attempts += 1;
            log("paste");
            if (policy.strict) event.preventDefault();
        };
        const onContextMenu = (event) => {
            stats.context_menu_attempts += 1;
            if (policy.strict) event.preventDefault();
        };
        const onFullscreen = () => {
            if (!document.fullscreenElement) {
                stats.fullscreen_exits += 1;
                log("fullscreen_exit");
                checkLimit();
            }
        };

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("blur", onBlur);
        window.addEventListener("focus", onFocus);
        document.documentElement.addEventListener("mouseleave", onMouseLeave);
        document.addEventListener("copy", onCopy);
        document.addEventListener("cut", onCopy);
        document.addEventListener("paste", onPaste);
        document.addEventListener("contextmenu", onContextMenu);
        document.addEventListener("fullscreenchange", onFullscreen);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("blur", onBlur);
            window.removeEventListener("focus", onFocus);
            document.documentElement.removeEventListener("mouseleave", onMouseLeave);
            document.removeEventListener("copy", onCopy);
            document.removeEventListener("cut", onCopy);
            document.removeEventListener("paste", onPaste);
            document.removeEventListener("contextmenu", onContextMenu);
            document.removeEventListener("fullscreenchange", onFullscreen);
        };
    }, [active, policy.strict, checkLimit]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Ask for fullscreen on desktop in strict mode (must be called from a click). */
    const requestFullscreen = useCallback(() => {
        if (!policy.strict || isTouchDevice()) return;
        const element = typeof document !== "undefined" ? document.documentElement : null;
        if (element && typeof element.requestFullscreen === "function") {
            element.requestFullscreen().catch(() => {});
        }
    }, [policy.strict]);

    /** Snapshot for the submit payload. Closes an away period still in progress. */
    const snapshot = useCallback(({ durationSeconds } = {}) => {
        endAway();
        const stats = statsRef.current;
        return {
            ...stats,
            events: stats.events.slice(0, MAX_EVENTS),
            strict: policy.strict,
            is_touch_device: isTouchDevice(),
            duration_seconds: durationSeconds != null
                ? durationSeconds
                : startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : null,
            // Field names the HR report already reads.
            tab_blur_count: leaveCount(stats),
            mouse_leave_count: stats.mouse_leaves,
        };
    }, [policy.strict]); // eslint-disable-line react-hooks/exhaustive-deps

    return { policy, warning, snapshot, requestFullscreen };
}
